import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { Helmet } from "react-helmet";
import { Button, VStack, useToast, Icon } from "@chakra-ui/react";
import LaborHackJobApplicationForm from "./laborhack-job-application-form";
import { FaArrowLeft } from "react-icons/fa";
import { RequestFailed } from "src/components/RequestFailed";
import { Loading } from "src/components/Loading";
import {
	CREATE_RECRUITER_JOB_APPLICATION_LOGGED_IN,
	GET_JOB_AD,
} from "src/features/jobs/graphql";
import { logEvent } from "src/utlis/analytics";
import { JobApplicationFormData } from "src/features/jobs/constants";
import { useProfile } from "src/hooks/useProfile";

const convertToISOString = (dateString: string): string => {
	if (!dateString) return dateString;
	const date = new Date(dateString);
	return date.toISOString();
};

/**
 * LaborHack (recruiter) job application page
 * @returns LaborHack (recruiter) job application page
 */
export default function LaborHackJobApplication() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const toast = useToast();

	const {
		profile,
		loading: profileLoading,
		error: profileError,
	} = useProfile();

	const { data, loading, error } = useQuery(GET_JOB_AD, {
		variables: { id: id! },
		skip: !id,
	});

	const [
		createRecruiterJobApplicationLoggedIn,
		{ loading: isCreatingApplication },
	] = useMutation(CREATE_RECRUITER_JOB_APPLICATION_LOGGED_IN, {
		onCompleted: (data) => {
			logEvent("job_application_submitted", {
				job_id: id,
				application_id: data.createRecruiterJobApplicationLoggedIn.id,
			});

			toast({
				title: "Application submitted",
				description:
					"Your LaborHack job application has been submitted successfully!",
				status: "success",
				duration: 5000,
				isClosable: true,
			});

			navigate(`/opportunities/laborhack/${id}`);
		},
		onError: (error) => {
			toast({
				title: "Application failed",
				description:
					error.message ||
					"Failed to submit your application. Please try again.",
				status: "error",
				duration: 5000,
				isClosable: true,
			});
		},
	});

	const handleSubmit = async (formData: JobApplicationFormData) => {
		try {
			const input = {
				ad: id!,
				email: profile?.email || "",
				firstName: profile?.firstName || "",
				lastName: profile?.lastName || "",
				phoneNumber: profile?.phoneNumber || "",
				yearsOfExperience: profile?.yearsOfExperience ?? 1,
				educationItems: formData.educationItems,
				employmentHistoryItems: formData.employmentHistoryItems.map((item) => ({
					...item,
					startedAt: convertToISOString(item.startedAt),
					endedAt: !!item.endedAt
						? convertToISOString(item.endedAt)
						: undefined,
				})),
				projectHistoryItems: formData.projectHistoryItems.map((item) => ({
					...item,
					startedAt: !!item.startedAt
						? convertToISOString(item.startedAt)
						: undefined,
					endedAt: convertToISOString(item.endedAt),
				})),
				certificationItems: formData.certificationItems.map((item) => ({
					...item,
					issuedAt: convertToISOString(item.issuedAt),
				})),
			};

			await createRecruiterJobApplicationLoggedIn({
				variables: { input },
			});
		} catch (error) {
			console.error("LaborHack job application submission error:", error);
		}
	};

	const handleCancel = () => {
		navigate(`/opportunities/laborhack/${id}`);
	};

	useEffect(() => {
		if (data?.getEmployerJobAd) {
			logEvent("job_application_page_viewed", {
				job_id: id,
				job_title: data.getEmployerJobAd.title,
			});
		}
	}, [data, id]);

	if (loading || profileLoading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<Loading />
			</div>
		);
	}

	if (error || !data?.getEmployerJobAd) {
		return <RequestFailed text={error?.message || "Job not found"} />;
	}

	if (profileError || !profile) {
		return (
			<RequestFailed
				text={
					profileError?.message ||
					"Profile not found. Please complete your profile first."
				}
			/>
		);
	}

	const job = data.getEmployerJobAd;

	if (job.hasApplied && !job.canApply) {
		return (
			<>
				<Helmet>
					<title>Application Submitted - {job.title} - LaborHack Jobs</title>
				</Helmet>
				<div>
					<h1>Application Submitted</h1>
					<p>You've already applied for this job opportunity!</p>
				</div>
			</>
		);
	}

	return (
		<>
			<Helmet>
				<title>Apply for {job.title} - LaborHack Jobs</title>
			</Helmet>
			<VStack spacing={0} maxW={"99vw"} minH={"100vh"} pb={8}>
				<section className="px-3 lg:px-20 w-full max-w-4xl">
					<div className="mb-8">
						<Button
							variant="link"
							colorScheme="prussianBlue"
							fontSize={{
								base: "14px",
								lg: "16px",
							}}
							onClick={handleCancel}
							className="mb-4 px-0"
							leftIcon={<Icon as={FaArrowLeft} w={4} h={4} />}
						>
							Back to job opportunity
						</Button>
						<h1 className="text-2xl lg:text-3xl font-bold text-[#0E212D] mb-2">
							Apply for {job.title}
						</h1>
						<p className="text-[#6E6E6E] text-lg">
							{job.companyName} • {job.location.area}, {job.location.state}
						</p>
					</div>

					<LaborHackJobApplicationForm
						onSubmit={handleSubmit}
						onCancel={handleCancel}
						isSubmitting={isCreatingApplication}
						jobId={id!}
					/>
				</section>
			</VStack>
		</>
	);
}
